
var baseUrl = ""

export function setBaseUrl(s: string) {
	if (s.endsWith("/")) baseUrl = s.substr(0, s.length - 1)
	else baseUrl = s
}

export class Failure {
	readonly err: string

	constructor(err: string) {
		this.err = err
	}
}

export const Backend = {
	fetchContentAvailability,
	deferPayment,
	createPaypalOrder,
	completePaypalOrder,
	redeemCode,
	cancelPaypalOrder,
	getTenant,
}



export type ContentAndStudent = {
	contentId: {
		tenantId: string
		classId: number | null
	}
	studentGid: string
}

export type EmailValues = {
	studentEmailAddress: string
	studentName: string
	tenantName: string
	className: string
	logoUrl: string
	productName: string
	supportEmailAddress: string
	companyAboutLink: string
	studentHelpLink: string
	privacyPolicyLink: string
	companyName: string
	companyAddress: string
}


// ==========================================


export type ContentAvailability = {
	contentTermId: number
	withinPurchasePeriod: boolean
	purchasePeriodStart: number
	studentTransactions: StudentTransactions
	paymentOptions: PaymentOptions
}

export type StudentTransactions = {
	alreadyDeferred: boolean
	alreadyPaid: boolean
	existingLicenseExpiry: number | null
}

export type PaymentOptions =  {
	paypal: PaypalOption | null
	deferred: DeferredOption | null
	redemptionCode: RedemptionCodeOption | null
}

export type PaypalOption = {
	expiry: number
	price: {
		amount: number
		currency: string
	}
}

export type DeferredOption = {
	expiry: number
}

export type RedemptionCodeOption = {
	expiry: number
}


async function fetchContentAvailability(contentAndStudent: ContentAndStudent): Promise<ContentAvailability | Failure> {
	return sendPost<ContentAvailability>("/fetchContentAvailability", contentAndStudent)
}

// ==========================================

export type Tenant = {
	id: string
	name: string
	paymentUrl: string
	redemptionUrl: string
}

async function getTenant(tenant: Tenant): Promise<Tenant | Failure> {
	return sendPost<Tenant>("/getTenant", tenant)
}

// ==========================================

async function deferPayment(contentAndStudent: ContentAndStudent, emailValues: EmailValues): Promise<unknown | Failure> {
	return sendPost("/deferPayment",  { contentAndStudent, emailValues })
}


// ==========================================


async function redeemCode(contentAndStudent: ContentAndStudent, redemptionCode: string, emailValues: EmailValues): Promise<unknown | Failure> {
	return sendPost("/redeemCode",  { contentAndStudent, redemptionCode, emailValues })
}


// ==========================================

export type CountryRegion = {
	country: string
	region: string | null
}

async function createPaypalOrder(contentAndStudent: ContentAndStudent, countryRegion: CountryRegion): Promise<string | Failure> {

	// This must be fully reconstructed since the argument will be a Vue proxy.
	const payload = {
		contentId: {
			tenantId: contentAndStudent.contentId.tenantId,
			classId: contentAndStudent.contentId.classId,
		},
		studentGid: contentAndStudent.studentGid,
		countryRegion: countryRegion
	}

	return sendPost<string>("createPaypalOrder", payload)
}

// ==========================================

export type StudentInfo = {
	name: string
	emailAddress: string
}

export type PrivateLabels = {
	logoUrl: string
	productName: string
	supportEmail: string
	companyAboutLink: string
	studentHelpLink: string
	privacyPolicyLink: string
	companyName: string
	companyAddress: string
}

async function completePaypalOrder(
	orderId: string,
	studentInfo: StudentInfo,
	privateLabels: PrivateLabels,
	tenantName: string,
	className: string,
): Promise<void | Failure> {

	const payload = {
		orderId,
		privateLabels,
		studentName: studentInfo.name,
		tenantName,
		className,
	}

	return sendPost<void>("completePaypalOrder", payload)
}

async function cancelPaypalOrder(orderId: string): Promise<void | Failure> {
	return sendPost("cancelPaypalOrder", { orderId })
}

// ==========================================

async function sendPost<T>(endpoint: string, data: object): Promise<T | Failure> {
	const response = await fetch(buildUrl(endpoint),
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(data)
		});

	const json = await response.json()

	if (response.ok) return json
	else {
		if ('err' in json) return new Failure(json.err)
		else return new Failure(response.statusText)
	}
}


function buildUrl(subPath: string): string {
	if (subPath.startsWith("/")) return baseUrl + subPath
	else return baseUrl + "/" + subPath
}
